import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  CatalogueView,
  ComponentType,
  ComponentTypeEnum,
  ConfigModule,
  FieldAutomationConfig,
  Languages,
  TemplateCategory,
} from './model';
import { configApi } from './api';
import { RootState } from '../root/root-types';

const CONFIQ_FEATURE_KEY = 'ConfigSettings';

export interface LastUpdated {
  lastUpdated: string;
  updatedDate: string;
}

export interface ConfigState {
  catologueView: CatalogueView;
  displayOutOfStock?: boolean;
  fieldAutomationConfig: FieldAutomationConfig;
  refreshTemplateList: boolean;
  lastUpdated?: LastUpdated[];
  isTemplateListLoading: boolean;
  templateName: string;
  templateCategory: TemplateCategory | '';
  templateLanguage: Languages | '';
  templateComponents: ComponentType[];
  selectedWabaUser: any;
}

const initialState: ConfigState = {
  catologueView: CatalogueView.ListView,
  displayOutOfStock: true,
  refreshTemplateList: false,
  lastUpdated: [],
  templateCategory: 'UTILITY',
  templateLanguage: 'en',
  templateName: '',
  templateComponents: [
    {
      type: ComponentTypeEnum.BODY,
      text: '',
      example: { body_text: [] },
    },
    {
      type: ComponentTypeEnum.CAROUSEL,
      cards: [
        {
          components: [
            {
              type: ComponentTypeEnum.BODY,
              text: '',
              example: { body_text: [] },
            },
            {
              type: ComponentTypeEnum.HEADER,
              format: 'IMAGE',
              example: { header_handle: [] },
            },
          ],
        },
      ],
    },
  ],
  fieldAutomationConfig: {
    enableGeoTagging: false,
    geoFencingRadius: 100,
    enableTripStockEntry: false,
    disableRetailingWithoutCheckIn: false,
  },
  isTemplateListLoading: false,
  selectedWabaUser: {},
};

export const configSlice = createSlice({
  name: CONFIQ_FEATURE_KEY,
  initialState,
  reducers: {
    updateCatalogConfig(state, action: PayloadAction<CatalogueView>) {
      state.catologueView = action.payload;
    },
    updateDisplayOutOfStock(state, action: PayloadAction<boolean | undefined>) {
      state.displayOutOfStock = action.payload;
    },
    updateFieldAutomationConfig(
      state,
      action: PayloadAction<Partial<FieldAutomationConfig>>
    ) {
      const oldConfig = state.fieldAutomationConfig || {
        enableGeoTagging: false,
        geoFencingRadius: 100,
        enableTripStockEntry: false,
        disableRetailingWithoutCheckIn: false,
      };
      state.fieldAutomationConfig = {
        ...oldConfig,
        ...action.payload,
      };
    },
    updateRefreshTemplateList(state, action: PayloadAction<boolean>) {
      state.refreshTemplateList = action.payload;
    },
    updateIsTemplateListLoading(state, action: PayloadAction<boolean>) {
      state.isTemplateListLoading = action.payload;
    },
    updateTemplateName(state, action: PayloadAction<string>) {
      state.templateName = action.payload;
    },
    updateTemplateLanguage(state, action: PayloadAction<Languages | ''>) {
      state.templateLanguage = action.payload;
    },
    updateTemplateCategory(
      state,
      action: PayloadAction<TemplateCategory | ''>
    ) {
      state.templateCategory = action.payload;
    },
    updateTemplateComponents(state, action: PayloadAction<ComponentType[]>) {
      state.templateComponents = action.payload;
    },
    updateSelectedWabaUser(state, action: PayloadAction<string>) {
      state.selectedWabaUser = action.payload;
    },
    updateLastUpdated(
      state,
      action: PayloadAction<{ lastUpdated: string; updatedDate: string }>
    ) {
      const { lastUpdated, updatedDate } = action.payload;
      const existingEntryIndex = state.lastUpdated?.findIndex(
        (entry) => entry.updatedDate === updatedDate
      );
      if (existingEntryIndex && existingEntryIndex !== -1) {
        if (state.lastUpdated && state.lastUpdated[existingEntryIndex]) {
          state.lastUpdated[existingEntryIndex] = {
            ...state.lastUpdated[existingEntryIndex],
            lastUpdated,
          };
        }
      } else {
        state.lastUpdated?.push({ lastUpdated, updatedDate });
      }
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      configApi.endpoints.getConfigDetails.matchFulfilled,
      (state, action) => {
        const originalArgs = action.meta.arg.originalArgs;
        const config = action?.payload?.config;
        switch (originalArgs.module) {
          case ConfigModule.CATALOGUE: {
            configSlice.caseReducers.updateCatalogConfig(state, {
              ...action,
              payload: config?.catalogueView ?? CatalogueView.ListView,
            });
            configSlice.caseReducers.updateDisplayOutOfStock(state, {
              ...action,
              payload: config?.displayOutOfStock,
            });
            break;
          }
          case ConfigModule.FIELD_AUTOMATION: {
            configSlice.caseReducers.updateFieldAutomationConfig(state, {
              ...action,
              payload: {
                enableGeoTagging: config.enableGeoTagging,
                disableRetailingWithoutCheckIn:
                  config.disableRetailingWithoutCheckIn,
                enableTripStockEntry: config.enableTripStockEntry,
                geoFencingRadius: config.geoFencingRadius,
              },
            });
            break;
          }
          default:
            break;
        }
      }
    );
  },
});

export const {
  updateCatalogConfig,
  updateDisplayOutOfStock,
  updateRefreshTemplateList,
  updateLastUpdated,
  updateIsTemplateListLoading,
  updateTemplateName,
  updateTemplateCategory,
  updateTemplateLanguage,
  updateSelectedWabaUser,
  updateTemplateComponents,
} = configSlice.actions;

export const getConfigState = (rootState: RootState): ConfigState =>
  rootState[CONFIQ_FEATURE_KEY];

export const getCatalogConfig = createSelector(
  getConfigState,
  (state) => state.catologueView
);

export const getDisplayOutOfStock = createSelector(
  getConfigState,
  (state) => state.displayOutOfStock
);

export const getFieldAutomationConfig = createSelector(
  getConfigState,
  (state) => state.fieldAutomationConfig
);

export const getRefreshTemplateList = createSelector(
  getConfigState,
  (state) => state.refreshTemplateList
);

export const getLastUpdated = createSelector(
  getConfigState,
  (state) => state.lastUpdated
);

export const getIsTemplateListLoading = createSelector(
  getConfigState,
  (state) => state.isTemplateListLoading
);
export const getTemplateName = createSelector(
  getConfigState,
  (state) => state.templateName
);
export const getTemplateCategory = createSelector(
  getConfigState,
  (state) => state.templateCategory
);
export const getTemplateLanguage = createSelector(
  getConfigState,
  (state) => state.templateLanguage
);
export const getTemplateComponents = createSelector(
  getConfigState,
  (state) => state.templateComponents
);
export const getSelectedWabaUser = createSelector(
  getConfigState,
  (state) => state.selectedWabaUser
);
